import { ShoppingCart, Receipt, QrCode, Shield, CreditCard, ChevronDown, ChevronUp, Package, GitCompareArrows, Loader2, Star } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { useState, useEffect, useRef } from "react";
import { ProductSpec } from "@/data/phoneSpecs";
import { useProducts } from "@/hooks/useProducts";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { supabase } from "@/integrations/supabase/client";
import PhoneSpecsModal from "@/components/PhoneSpecsModal";
import PhoneComparison from "@/components/PhoneComparison";

// Taxas InfinityPay - Plano Inicial (até 20 mil/mês) Visa/MC
const creditRates: Record<number, number> = {
  1: 3.15, 2: 5.39, 3: 6.12, 4: 6.85, 5: 7.57, 6: 8.28,
  7: 8.99, 8: 9.69, 9: 10.38, 10: 11.06, 11: 11.74, 12: 12.40,
};

const formatCurrency = (value: number) =>
  value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const ProductCard = ({ product, onOpenSpecs, index }: { product: ProductSpec; onOpenSpecs: (p: ProductSpec) => void; index: number }) => {
  const { addItem } = useCart();
  const [withInsurance, setWithInsurance] = useState(false);
  const [showInstallments, setShowInstallments] = useState(false);

  const pixPrice = product.priceNum * 0.95;
  const boletoPrice = product.priceNum * 0.97;
  const carnePrice = product.priceNum * 2;

  const getInstallmentTotal = (parcelas: number) => {
    const rate = creditRates[parcelas] / 100;
    return product.priceNum * (1 + rate);
  };

  const ratings = (product.ratings || {}) as Partial<import("@/data/phoneSpecs").PhoneRatings>;
  const ratingItems = [
    { label: "Tela", value: ratings.screen },
    { label: "Perf.", value: ratings.performance },
    { label: "Cam.", value: ratings.camera },
    { label: "Bat.", value: ratings.battery },
    { label: "C/B", value: ratings.value },
  ];
  const overall = ratings.overall;

  return (
    <div className="group relative flex bg-card rounded-xl border border-border hover:border-gold transition-all duration-300 overflow-hidden hover:shadow-gold hover:-translate-y-1">
      {/* Left ratings strip */}
      <div className="hidden sm:flex flex-col items-center justify-center gap-1.5 px-2.5 py-4 bg-secondary/50 border-r border-border min-w-[56px]">
        <div className="flex items-center gap-0.5 mb-1">
          <Star className="w-3 h-3 text-primary fill-primary" />
          <span className="text-xs font-bold text-foreground">{(overall ?? 0).toFixed(1)}</span>
        </div>
        <div className="w-5 h-px bg-border" />
        {ratingItems.map((r) => (
          <div key={r.label} className="flex flex-col items-center gap-0.5">
            <span className="text-[9px] text-muted-foreground leading-none">{r.label}</span>
            <div className="w-7 h-1 bg-secondary rounded-full overflow-hidden">
              <div
                className="h-full bg-primary/70 rounded-full"
                style={{ width: `${((r.value ?? 0) / 10) * 100}%` }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Main card content */}
      <div className="flex-1 min-w-0">
        <div className="absolute top-3 right-3 z-10 flex items-center gap-1 bg-secondary/90 backdrop-blur-sm text-foreground text-xs font-semibold px-2.5 py-1 rounded-full border border-border">
          <Package className="w-3 h-3" />
          {product.stock} em estoque
        </div>
        <div
          className="relative aspect-square overflow-hidden bg-secondary cursor-pointer"
          onClick={() => onOpenSpecs(product)}
        >
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
          />
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <p className="text-xs text-white font-medium text-center">Toque para ver especificações</p>
          </div>
        </div>

        {/* Mobile ratings row */}
        <div className="flex sm:hidden items-center gap-3 px-5 pt-3">
          <div className="flex items-center gap-1">
            <Star className="w-3.5 h-3.5 text-primary fill-primary" />
            <span className="text-sm font-bold text-foreground">{(overall ?? 0).toFixed(1)}</span>
          </div>
          <div className="flex items-center gap-2 flex-1">
            {ratingItems.map((r) => (
              <div key={r.label} className="flex flex-col items-center gap-0.5 flex-1">
                <span className="text-[8px] text-muted-foreground leading-none">{r.label}</span>
                <div className="w-full h-1 bg-secondary rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary/70 rounded-full"
                    style={{ width: `${((r.value ?? 0) / 10) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="p-5">
          <h3 className="font-display text-lg font-semibold text-foreground">{product.name}</h3>
          <p className="text-sm text-muted-foreground mb-2">Cor: {product.color}</p>
          <p className="text-2xl font-bold text-gradient-gold mb-3">{product.price}</p>

          <div className="space-y-2 mb-4">
            <div className="flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-lg px-3 py-2">
              <QrCode className="w-4 h-4 text-primary flex-shrink-0" />
              <div>
                <span className="text-sm font-bold text-primary">Pix: {formatCurrency(pixPrice)}</span>
                <span className="text-xs text-primary/80 ml-1">(5% OFF)</span>
              </div>
            </div>
            <div className="flex items-center gap-2 bg-accent/50 border border-accent rounded-lg px-3 py-2">
              <Receipt className="w-4 h-4 text-accent-foreground flex-shrink-0" />
              <div>
                <span className="text-sm font-bold text-accent-foreground">Boleto: {formatCurrency(boletoPrice)}</span>
                <span className="text-xs text-accent-foreground/80 ml-1">(3% OFF)</span>
              </div>
            </div>

            {/* Credit card installments */}
            <div className="border border-border rounded-lg overflow-hidden">
              <button
                onClick={() => setShowInstallments(!showInstallments)}
                className="w-full flex items-center gap-2 px-3 py-2 bg-secondary hover:bg-secondary/80 transition-colors"
              >
                <CreditCard className="w-4 h-4 text-foreground flex-shrink-0" />
                <span className="text-sm font-semibold text-foreground flex-1 text-left">
                  Crédito: até 12x de {formatCurrency(getInstallmentTotal(12) / 12)}
                </span>
                {showInstallments ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
              </button>
              {showInstallments && (
                <div className="px-3 py-2 space-y-1 bg-card max-h-48 overflow-y-auto animate-[accordion-down_0.2s_ease-out]">
                  {Object.entries(creditRates).map(([parcela, taxa]) => {
                    const n = Number(parcela);
                    const total = getInstallmentTotal(n);
                    const valorParcela = total / n;
                    return (
                      <div key={parcela} className="flex justify-between text-xs text-muted-foreground">
                        <span>{n}x de {formatCurrency(valorParcela)}</span>
                        <span className="text-foreground/60">({taxa}% — total {formatCurrency(total)})</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <p className="text-xs text-muted-foreground text-center">
              Carnê: 12x de {formatCurrency(carnePrice / 12)} (total {formatCurrency(carnePrice)})
            </p>
          <p className="text-xs text-primary text-center font-semibold flex items-center justify-center gap-1 w-full">
            <Shield className="w-3 h-3 flex-shrink-0" /> Carnê inclui seguro obrigatório (apenas Android)
          </p>
          </div>

          {/* Insurance toggle */}
          <label className="flex items-center gap-2 mb-4 cursor-pointer bg-secondary rounded-lg px-3 py-2.5 border border-border hover:border-primary transition-colors">
            <input
              type="checkbox"
              checked={withInsurance}
              onChange={(e) => setWithInsurance(e.target.checked)}
              className="w-4 h-4 rounded accent-primary"
            />
            <Shield className="w-4 h-4 text-primary flex-shrink-0" />
            <div className="flex-1">
              <span className="text-sm font-semibold text-foreground">Adicionar Seguro</span>
              <p className="text-xs text-muted-foreground">Furto e Roubo — R$ 49,99/mês</p>
            </div>
          </label>

          <button
            onClick={() => {
              addItem({ id: product.id, name: `${product.name} - ${product.color}`, price: product.price, priceNum: product.priceNum, image: product.image });
              if (withInsurance) {
                addItem({ id: `seguro-${product.id}`, name: `Seguro Premium - ${product.name}`, price: "R$ 49,99/mês", priceNum: 49.99, image: product.image });
              }
            }}
            className="w-full flex items-center justify-center gap-2 bg-gradient-gold text-primary-foreground py-3 rounded-lg font-semibold hover:opacity-90 transition-all hover:scale-[1.02] active:scale-[0.98]"
          >
            <ShoppingCart className="w-4 h-4" />
            Comprar
          </button>
        </div>
      </div>
    </div>
  );
};

const ProductGrid = () => {
  const [specsProduct, setSpecsProduct] = useState<ProductSpec | null>(null);
  const [comparisonOpen, setComparisonOpen] = useState(false);
  const { data: products = [], isLoading } = useProducts();
  const trackedRef = useRef(false);

  // Track product views once products load
  useEffect(() => {
    if (products.length > 0 && !trackedRef.current) {
      trackedRef.current = true;
      const rows = products.map((p) => ({ product_id: p.id }));
      supabase.from("product_views").insert(rows).then(() => {});
    }
  }, [products]);

  return (
    <section id="celulares" className="py-20 bg-background">
      <div className="container mx-auto px-4">
        <div className="text-center mb-14">
          <h2 className="font-display text-4xl md:text-5xl font-bold text-gradient-gold mb-4">Nossos Celulares</h2>
          <p className="text-muted-foreground text-lg max-w-xl mx-auto">
            Smartphones das melhores marcas com garantia e condições imperdíveis
          </p>
          {products.length >= 2 && (
            <button
              onClick={() => setComparisonOpen(true)}
              className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 bg-secondary border border-border rounded-lg text-sm font-semibold text-foreground hover:border-primary hover:scale-105 transition-all"
            >
              <GitCompareArrows className="w-4 h-4 text-primary" />
              Comparar Aparelhos
            </button>
          )}
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : products.length === 0 ? (
          <p className="text-center text-muted-foreground py-12">Nenhum celular disponível no momento.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 max-w-3xl mx-auto">
            {products.map((product, i) => (
              <ProductCard key={product.id} product={product} onOpenSpecs={setSpecsProduct} index={i} />
            ))}
          </div>
        )}
      </div>

      <PhoneSpecsModal open={!!specsProduct} onOpenChange={(o) => !o && setSpecsProduct(null)} product={specsProduct} />
      <PhoneComparison open={comparisonOpen} onOpenChange={setComparisonOpen} products={products} />
    </section>
  );
};

export default ProductGrid;
